/**
 * Responsive image helpers used to reduce bytes shipped to mobile and
 * improve LCP. Supports three URL families:
 *   - Unsplash (?auto=format&fit=crop&w=…&q=…)
 *   - Supabase Storage (public URLs → /render/image/public with width/quality)
 *   - Cangooroo / T4W CDN and unknown hosts (pass-through)
 *
 * All helpers are idempotent and safe for any string input.
 */

export interface ResponsiveImageUrls {
  src: string;
  srcSet: string;
  sizes: string;
}

type Provider = "unsplash" | "supabase" | "weserv" | "other";

const detectProvider = (url: string): Provider => {
  if (!url) return "other";
  if (url.includes("images.unsplash.com")) return "unsplash";
  if (url.includes("images.weserv.nl")) return "weserv";
  // Supabase public storage URL: /storage/v1/object/public/<bucket>/…
  if (/\/storage\/v1\/object\/public\//.test(url)) return "supabase";
  // Route http(s) URLs from unknown CDNs (Cangooroo, T4W, etc.) through
  // the free weserv.nl image proxy so we can emit responsive srcSet and
  // shrink mobile payloads. Skip non-http (data:, blob:, relative) URLs.
  if (/^https?:\/\//i.test(url)) return "weserv";
  return "other";
};

/**
 * Build a URL for a given width/quality. If the provider cannot be
 * transformed, the original URL is returned unchanged.
 */
export const getOptimizedUrl = (
  url: string,
  width: number,
  quality = 70,
): string => {
  if (!url) return url;
  const provider = detectProvider(url);

  if (provider === "unsplash") {
    const photoId = url.match(/photo-[a-zA-Z0-9-]+/)?.[0];
    if (!photoId) return url;
    return `https://images.unsplash.com/${photoId}?auto=format&fit=crop&w=${width}&q=${quality}`;
  }

  if (provider === "supabase") {
    const transformed = url.replace(
      "/storage/v1/object/public/",
      "/storage/v1/render/image/public/",
    );
    const sep = transformed.includes("?") ? "&" : "?";
    return `${transformed}${sep}width=${width}&quality=${quality}&resize=cover`;
  }

  if (provider === "weserv") {
    // weserv.nl expects the source URL without scheme. If we've already
    // wrapped a URL, don't wrap again — return as-is.
    if (url.includes("images.weserv.nl")) return url;
    const stripped = url.replace(/^https?:\/\//i, "");
    const encoded = encodeURIComponent(stripped);
    return `https://images.weserv.nl/?url=${encoded}&w=${width}&q=${quality}&output=webp&we`;
  }

  return url;
};

/**
 * Back-compat alias used across the codebase.
 */
export const getMobileOptimizedUrl = (
  baseUrl: string,
  width: number = 800,
  quality: number = 60,
): string => getOptimizedUrl(baseUrl, width, quality);

/**
 * Generate a responsive srcSet + sizes for a given base URL.
 */
export const getResponsiveImageUrls = (
  baseUrl: string,
  sizes: string = "100vw",
  widths: readonly number[] = [400, 640, 960, 1280, 1920],
  quality = 65,
): ResponsiveImageUrls => {
  if (!baseUrl) return { src: baseUrl, srcSet: "", sizes };
  const provider = detectProvider(baseUrl);

  if (provider === "other") {
    return { src: baseUrl, srcSet: "", sizes };
  }

  const srcSet = widths
    .map((w) => `${getOptimizedUrl(baseUrl, w, quality)} ${w}w`)
    .join(", ");
  const src = getOptimizedUrl(baseUrl, widths[0], quality);
  return { src, srcSet, sizes };
};

/**
 * Back-compat: return only the srcset string.
 */
export const generateSrcSet = (baseUrl: string): string =>
  getResponsiveImageUrls(baseUrl).srcSet;

/**
 * Preset `sizes` strings for common layouts. Using these keeps the
 * browser from picking the largest candidate on mobile.
 */
export const IMAGE_SIZES = {
  hero: "100vw",
  // 1 col on mobile, 2 on md, 3 on lg (typical card grid)
  card: "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw",
  // Full-width on mobile, 2 cols on tablet, 4 cols on desktop (dense grids)
  smallCard: "(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw",
  // Sidebar / detail thumbnail
  thumb: "(max-width: 640px) 25vw, 160px",
} as const;
